import { Button, Form, Input, Select } from 'antd';
import { Html5Qrcode } from 'html5-qrcode';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiBarcode } from 'react-icons/ci';
import { LuImageUp } from 'react-icons/lu';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import './BarcodeScanner.scss';

interface BarcodeScannerProps {
  fps?: number;
  qrbox?: number | { width: number; height: number };
  aspectRatio?: number;
  verbose?: boolean;
  scannerId?: string;
  onScanSuccess: (decodedText: string, decodedResult: any) => void;
  onScanFailure?: (error: any) => void;
  errorNotification?: (_message: string, _params?: any) => void;
  allowManualInput?: boolean;
}

interface CameraDevice {
  id: string;
  label: string;
}

export enum PermissionStatus {
  GRANTED = 'granted',
  DENIED = 'denied',
  PROMPT = 'prompt',
  CHECKING = 'checking',
}

const BarcodeScanner = (props: BarcodeScannerProps) => {
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const [manualBarcode, setManualBarcode] = useState('');
  const [domId] = useState(
    props.scannerId || `reader-${Math.random().toString(36).substring(2, 9)}`
  );
  const { t } = useTranslation('barcodeScanner');
  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>(
    PermissionStatus.CHECKING
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanMode, setScanMode] = useState<'camera' | 'image' | 'manual'>('camera');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showBarcodeImage, setShowBarcodeImage] = useState<boolean>(false);
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);

  // Initialize Html5Qrcode instance
  useEffect(() => {
    if (!document.getElementById(domId)) {
      return;
    }

    try {
      const html5QrCode = new Html5Qrcode(domId, {
        verbose: props.verbose,
      });
      html5QrCodeRef.current = html5QrCode;
    } catch (e) {
      console.error('Error initializing Html5Qrcode:', e);
      setError(t('failedToInitializeScanner'));
    }

    return () => {
      stopScanning();
    };
  }, [domId, props.verbose]);

  // Check camera permissions
  useEffect(() => {
    checkCameraPermissions();
  }, []);

  const checkCameraPermissions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const devices = await Html5Qrcode.getCameras();

      if (!devices || devices.length === 0) {
        setPermissionStatus(PermissionStatus.DENIED);
        setError(t('noCamerasFoundError'));
        props.errorNotification?.('noCameraFoundAlert');
        setIsLoading(false);
        return;
      }

      const cameraDevices: CameraDevice[] = devices.map((device) => ({
        id: device.id,
        label: device.label || `Camera ${device.id}`,
      }));

      setCameras(cameraDevices);
      setPermissionStatus(PermissionStatus.GRANTED);

      // Auto-select rear camera by default
      if (cameraDevices.length > 0) {
        const rearKeywords = ['back', 'rear', 'environment', 'outer', 'posterior', 'facing back'];
        const rearCamera = cameraDevices.find((device) =>
          rearKeywords.some((keyword) => device.label.toLowerCase().includes(keyword))
        );

        if (rearCamera) {
          setSelectedCameraId(rearCamera.id);
        } else if (cameraDevices.length > 1) {
          // On mobile devices with multiple cameras, rear camera is typically listed last
          setSelectedCameraId(cameraDevices[cameraDevices.length - 1].id);
        } else {
          setSelectedCameraId(cameraDevices[0].id);
        }
      }

      setIsLoading(false);
    } catch (err: any) {
      console.error('Camera permission error:', err);
      setIsLoading(false);

      // Handle error types
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionStatus(PermissionStatus.DENIED);
        setError(t('cameraPermissionDeniedError'));
        props.errorNotification?.(t('cameraPermissionDenied'));
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setPermissionStatus(PermissionStatus.GRANTED);
        setError(t('noCamerasFoundError'));
        props.errorNotification?.(t('noCameraFoundAlert'));
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setPermissionStatus(PermissionStatus.GRANTED);
        setError(t('cameraUsedByAnotherApp'));
        props.errorNotification?.(t('cameraAlreadyInUse'));
      } else if (
        err.name === 'OverconstrainedError' ||
        err.name === 'ConstraintNotSatisfiedError'
      ) {
        setPermissionStatus(PermissionStatus.GRANTED);
        setError(t('cameraConstraintsNotSatisfied'));
        props.errorNotification?.(t('cameraNotAccessible'));
      } else if (err.name === 'NotSupportedError') {
        setPermissionStatus(PermissionStatus.GRANTED);
        setError(t('cameraNotSupportedByBrowser'));
        props.errorNotification?.('cameraNotSupported');
      } else {
        setPermissionStatus(PermissionStatus.GRANTED);
        setError(t('checkCameraPermissions'));
        props.errorNotification?.(t('cameraNotAccessible'));
      }
    }
  };

  const startScanning = async () => {
    if (!html5QrCodeRef.current || !selectedCameraId) {
      console.error('Scanner not initialized or no camera selected');
      return;
    }

    if (isScanning) {
      console.warn('Already scanning');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const config = {
        fps: props.fps || 20,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
          return {
            width: minEdge * 0.8,
            height: minEdge * 0.4,
          };
        },
        disableFlip: false,
      };
      setIsScanning(true);

      await html5QrCodeRef.current.start(
        selectedCameraId,
        config,
        (decodedText, decodedResult) => {
          // Success callback
          stopScanning();
          props.onScanSuccess(decodedText, decodedResult);
        },
        (errorMessage) => {
          // Error callback
          if (props.onScanFailure) {
            props.onScanFailure(errorMessage);
          }
        }
      );

      setIsLoading(false);
    } catch (err: any) {
      console.error('Error starting scanner:', err);
      setIsLoading(false);
      setIsScanning(false);

      // Handle start errors
      if (err.name === 'NotAllowedError') {
        setError(t('cameraPermissionDenied'));
        props.errorNotification?.(t('cameraPersmissionDenied'));
      } else if (err.name === 'NotFoundError') {
        setError(t('selectedCameraNotFound'));
      } else if (err.name === 'NotReadableError') {
        setError(t('cameraAlreadyInUse'));
      } else {
        setError(`${t('failedToStartScan')} ${err.message || t('unknownError')}`);
        props.errorNotification?.(t('failedToScan'));
      }
    }
  };

  const stopScanning = async () => {
    if (!html5QrCodeRef.current) {
      return;
    }

    try {
      // Scanner states as per documentation: 0 = Unknown, 2 = SCANNING, 3 = PAUSED
      const scannerState = html5QrCodeRef.current.getState();

      if (scannerState !== 2) {
        setIsScanning(false);
        return;
      }

      await html5QrCodeRef.current.stop();
      setIsScanning(false);
    } catch (err) {
      setIsScanning(false);
    }
  };

  const handleCameraChange = async (cameraId: string) => {
    if (cameraId === selectedCameraId) {
      return;
    }

    const wasScanning = isScanning;

    // Stop current scanning if active
    if (wasScanning) {
      await stopScanning();
    }

    // Update selected camera
    setSelectedCameraId(cameraId);

    // Restart scanning with new camera if it was previously scanning
    if (wasScanning) {
      setTimeout(() => {
        startScanning();
      }, 300);
    }
  };

  const requestPermissions = async () => {
    await checkCameraPermissions();
  };

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !html5QrCodeRef.current) return;

    setIsLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    try {
      const decodedResult = await html5QrCodeRef.current.scanFileV2(file, false);

      props.onScanSuccess(decodedResult.decodedText, decodedResult);

      setUploadedImagePreview(null);
      setShowBarcodeImage(false);
    } catch (err: any) {
      setShowBarcodeImage(true);
      console.error('Error scanning file:', err);
      setError(t('noBarcodeFoundInImage'));
      props.errorNotification?.(t('failedToScanImage'));
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleManualSubmit = () => {
    const trimmed = manualBarcode.trim();
    if (trimmed) {
      props.onScanSuccess(trimmed, { manualInput: true });
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px',
            marginBottom: '12px',
          }}
        >
          <strong>{t('error')}</strong> {error}
        </div>
      )}

      <div
        id={domId}
        style={{
          width: '100%',
          display:
            scanMode === 'camera' && permissionStatus === PermissionStatus.GRANTED
              ? 'block'
              : 'none',
        }}
      />

      {scanMode === 'camera' && (
        <>
          {permissionStatus === PermissionStatus.DENIED && (
            <div
              className="flex flex-col gap-4"
              style={{
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                marginBottom: '12px',
              }}
            >
              <p style={{ marginBottom: '12px' }}>{t('cameraAccessRequired')}</p>
              <PrimaryButton handleOnClick={requestPermissions}>
                {t('requestCameraPermission')}
              </PrimaryButton>
              <SecondaryButton
                handleOnClick={() => {
                  setScanMode('image');
                  setShowBarcodeImage(false);
                  setError('');
                }}
              >
                {t('changeToImageUpload')}
              </SecondaryButton>
              <SecondaryButton
                handleOnClick={() => {
                  setScanMode('manual');
                  setError('');
                }}
              >
                {t('enterBarcodeManually', 'Enter Barcode Manually')}
              </SecondaryButton>
            </div>
          )}

          {/* Camera selection */}
          {permissionStatus === PermissionStatus.GRANTED && cameras.length > 1 && (
            <Form.Item label={t('selectCamera')} style={{ marginBottom: 12, marginTop: 5 }}>
              <Select
                value={selectedCameraId}
                onChange={handleCameraChange}
                disabled={isScanning}
                style={{ width: '100%', maxWidth: 220 }}
              >
                {cameras.map((camera) => (
                  <Select.Option key={camera.id} value={camera.id}>
                    {camera.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Scanner controls */}
          {permissionStatus === PermissionStatus.GRANTED && (
            <div>
              {!isScanning ? (
                <div className="mx-auto flex flex-col gap-4 pt-5">
                  <div className="aspect-rectangle mx-auto flex w-80 items-center justify-center border border-gray-200 p-8">
                    <CiBarcode size={75} />
                  </div>

                  <PrimaryButton
                    handleOnClick={startScanning}
                    disabled={isLoading || !selectedCameraId}
                  >
                    {isLoading ? t('starting') : t('startScanning')}
                  </PrimaryButton>
                  <SecondaryButton
                    handleOnClick={() => {
                      setScanMode('image');
                      setShowBarcodeImage(false);
                      setError('');
                    }}
                  >
                    {t('changeToImageUpload')}
                  </SecondaryButton>
                  <SecondaryButton
                    handleOnClick={() => {
                      setScanMode('manual');
                      setError('');
                    }}
                  >
                    {t('enterBarcodeManually', 'Enter Barcode Manually')}
                  </SecondaryButton>
                </div>
              ) : (
                <div className="mx-auto flex flex-col gap-4 pt-5">
                  <SecondaryButton handleOnClick={stopScanning}>{t('stopCamera')}</SecondaryButton>
                </div>
              )}
            </div>
          )}

          {isLoading && permissionStatus === PermissionStatus.CHECKING && (
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <p>{t('checkingCameraPermissions')}</p>
            </div>
          )}
        </>
      )}

      {scanMode === 'image' && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <div className="flex flex-col gap-4 pt-5">
            {uploadedImagePreview && showBarcodeImage ? (
              <>
                <div className="mx-auto w-80 overflow-hidden rounded-lg border border-gray-200">
                  <img
                    src={uploadedImagePreview}
                    alt="Uploaded barcode"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="aspect-rectangle mx-auto flex w-80 items-center justify-center border border-gray-200 p-8">
                <LuImageUp size={75} />
              </div>
            )}
            <PrimaryButton handleOnClick={triggerFileSelect} disabled={isLoading}>
              {isLoading ? t('scanning') : t('uploadImage')}
            </PrimaryButton>
            <SecondaryButton
              handleOnClick={() => {
                setScanMode('camera');
                setError('');
                setUploadedImagePreview(null);
                setShowBarcodeImage(false);
              }}
            >
              {t('changeToCameraScan')}
            </SecondaryButton>
            <SecondaryButton
              handleOnClick={() => {
                setScanMode('manual');
                setError('');
                setUploadedImagePreview(null);
                setShowBarcodeImage(false);
              }}
            >
              {t('enterBarcodeManually', 'Enter Barcode Manually')}
            </SecondaryButton>
          </div>
        </>
      )}

      {scanMode === 'manual' && (
        <div className="flex flex-col gap-4 pt-5">
          <div className="aspect-rectangle mx-auto flex w-80 items-center justify-center border border-gray-200 p-8">
            <CiBarcode size={75} />
          </div>

          <div className="flex gap-2">
            <Input
              placeholder={t('enterBarcodeNumber', 'Enter barcode number...')}
              value={manualBarcode}
              onChange={(e) => setManualBarcode(e.target.value)}
              onPressEnter={handleManualSubmit}
            />
            <Button type="primary" onClick={handleManualSubmit}>
              {t('submit', 'Submit')}
            </Button>
          </div>

          <SecondaryButton
            handleOnClick={() => {
              setScanMode('camera');
              setError('');
            }}
          >
            {t('changeToCameraScan')}
          </SecondaryButton>
          <SecondaryButton
            handleOnClick={() => {
              setScanMode('image');
              setError('');
            }}
          >
            {t('changeToImageUpload')}
          </SecondaryButton>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
